import { api } from "@/lib/apiClient";
import { userHasGroup, userIsBuero, type CurrentUser } from "@/lib/auth";
import {
  DOKUMENT_STATUS_LABELS,
  TIMESHEET_STATUS_LABELS,
  type DokumentStatus,
  type TimesheetListStatus,
} from "@/lib/dokumente";
import { lieferscheinBillingStatusLabel } from "@/lib/lieferscheine";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";

export type StatusSichtbarkeitLevel = "alle" | "buero";

export type StatusSichtbarkeitDomain =
  | "projekte"
  | "dokumente"
  | "stundennachweise"
  | "lieferscheine";

export type StatusSichtbarkeitConfig = Record<
  StatusSichtbarkeitDomain,
  Record<string, StatusSichtbarkeitLevel>
>;

export const STATUS_SICHTBARKEIT_LEVEL_LABELS: Record<StatusSichtbarkeitLevel, string> = {
  alle: "Alle",
  buero: "Nur Büro",
};

export const STATUS_SICHTBARKEIT_DOMAINS: StatusSichtbarkeitDomain[] = [
  "projekte",
  "dokumente",
  "stundennachweise",
  "lieferscheine",
];

export const STATUS_SICHTBARKEIT_DOMAIN_LABELS: Record<StatusSichtbarkeitDomain, string> = {
  projekte: "Projekte",
  dokumente: "Dokumente",
  stundennachweise: "Stundennachweise",
  lieferscheine: "Lieferscheine",
};

export const DOMAIN_STATUS_KEYS: Record<StatusSichtbarkeitDomain, readonly string[]> = {
  projekte: PROJECT_WORKFLOW_STATUSES,
  dokumente: Object.keys(DOKUMENT_STATUS_LABELS) as DokumentStatus[],
  stundennachweise: Object.keys(TIMESHEET_STATUS_LABELS) as TimesheetListStatus[],
  lieferscheine: ["signed", "assigned", "invoice_draft", "invoice_created", "archived"],
};

export function normalizeStatusSichtbarkeitLevel(
  value?: string | null,
): StatusSichtbarkeitLevel {
  return value === "buero" ? "buero" : "alle";
}

export function userCanSeeBueroStatuses(user: CurrentUser | null): boolean {
  return userIsBuero(user) || userHasGroup(user, "App-Admins");
}

export function defaultStatusSichtbarkeit(): StatusSichtbarkeitConfig {
  return {
    projekte: Object.fromEntries(
      DOMAIN_STATUS_KEYS.projekte.map((key) => [key, "alle" as const]),
    ),
    dokumente: Object.fromEntries(
      DOMAIN_STATUS_KEYS.dokumente.map((key) => [key, "alle" as const]),
    ),
    stundennachweise: Object.fromEntries(
      DOMAIN_STATUS_KEYS.stundennachweise.map((key) => [key, "alle" as const]),
    ),
    lieferscheine: Object.fromEntries(
      DOMAIN_STATUS_KEYS.lieferscheine.map((key) => [key, "alle" as const]),
    ),
  };
}

export function normalizeStatusSichtbarkeit(
  raw?: Partial<StatusSichtbarkeitConfig> | null,
): StatusSichtbarkeitConfig {
  const base = defaultStatusSichtbarkeit();
  if (!raw) return base;
  for (const domain of STATUS_SICHTBARKEIT_DOMAINS) {
    const domainRaw = raw[domain];
    if (!domainRaw || typeof domainRaw !== "object") continue;
    for (const key of DOMAIN_STATUS_KEYS[domain]) {
      if (key in domainRaw) {
        base[domain][key] = normalizeStatusSichtbarkeitLevel(domainRaw[key]);
      }
    }
  }
  return base;
}

export function statusLabelForDomain(domain: StatusSichtbarkeitDomain, status: string): string {
  switch (domain) {
    case "projekte":
      return (
        PROJECT_WORKFLOW_STATUS_LABELS[status as ProjectWorkflowStatusKey] || status
      );
    case "dokumente":
      return DOKUMENT_STATUS_LABELS[status as DokumentStatus] || status;
    case "stundennachweise":
      return TIMESHEET_STATUS_LABELS[status as TimesheetListStatus] || status;
    case "lieferscheine":
      return lieferscheinBillingStatusLabel(status);
    default:
      return status;
  }
}

export function visibleStatusesForUser(
  domain: StatusSichtbarkeitDomain,
  config: StatusSichtbarkeitConfig | null | undefined,
  user: CurrentUser | null,
): string[] {
  const keys = [...DOMAIN_STATUS_KEYS[domain]];
  if (userCanSeeBueroStatuses(user)) return keys;
  const domainCfg = config?.[domain] || {};
  return keys.filter(
    (key) => normalizeStatusSichtbarkeitLevel(domainCfg[key]) === "alle",
  );
}

export async function getStatusSichtbarkeit(): Promise<StatusSichtbarkeitConfig> {
  const res = await api.get<{ status_sichtbarkeit: StatusSichtbarkeitConfig }>(
    "/einstellungen/status-sichtbarkeit",
  );
  return normalizeStatusSichtbarkeit(res.status_sichtbarkeit);
}

export async function saveStatusSichtbarkeit(
  config: StatusSichtbarkeitConfig,
): Promise<StatusSichtbarkeitConfig> {
  const res = await api.put<{ status_sichtbarkeit: StatusSichtbarkeitConfig }>(
    "/einstellungen/status-sichtbarkeit",
    config,
  );
  return normalizeStatusSichtbarkeit(res.status_sichtbarkeit);
}
